// A seção "Impacto nos serviços" do painel de regra (spec rule-impact-preview): antes de
// reprogramar, a lista dos serviços cujo desembolso vai mudar — caminho na EAP, peso do
// insumo, valor redistribuído e a curva antes × depois —, os que o alvo toca mas uma regra
// mais específica já governa, e o total da obra, que não muda. Nada é gravado aqui.
import { ampliInconsistentReasonLabel } from "../financialProjection/cashFlowPlanning";
import { competenceText, money, percentLabel } from "./format";

const keyLabel = (key) => {
  const text = String(key ?? "");
  if (text.startsWith("resource:")) return `insumo ${text.slice("resource:".length)}`;
  if (text.startsWith("resource_group:ampli:")) return "um grupo seu";
  if (text.startsWith("resource_group:erp:")) return `a família ${text.slice("resource_group:erp:".length)}`;
  if (text.startsWith("node:")) return `o nível ${text.slice("node:".length)} da EAP`;
  return "outra regra";
};

/**
 * O caminho na EAP do ORÇAMENTO ("05 SUPRAESTRUTURA › 05.004 CONCRETO › …"), que é o que o
 * cliente lê no orçamento. Uma resposta sem os códigos (servidor anterior) cai nos ids.
 */
const pathText = (service) =>
  Array.isArray(service.path) && service.path.length > 0
    ? service.path.map((node) => (node.label ? `${node.wbs} ${node.label}` : node.wbs)).join(" › ")
    : (service.pathCodes ?? []).join(" › ");

/** `money()` lê o contexto do projeto: cada linha é uma folha, com as chamadas no topo. */
const ImpactedRow = ({ service }) => {
  const redistributed = money(service.redistributedCents);
  const months = [...new Set([...service.before.map(([m]) => m), ...service.after.map(([m]) => m)])].sort();
  const beforeBy = new Map(service.before);
  const afterBy = new Map(service.after);
  const lines = months.map((month) => ({
    month,
    before: money(beforeBy.get(month) ?? null),
    after: money(afterBy.get(month) ?? null),
  }));
  return (
    <li className="ampli-impact__service" data-testid="impact-service">
      <details>
        <summary>
          <span className="ampli-impact__title">
            {service.wbs ? (
              <span className="ampli-impact__code" data-testid="impact-wbs">{service.wbs}</span>
            ) : null}
            <strong>{service.label}</strong>
          </span>
          <span className="ampli-impact__path" data-testid="impact-path">{pathText(service)}</span>
          <span className="ampli-impact__facts">
            <span className="ampli-impact__fact">{`Peso do insumo ${percentLabel(service.weightBps)}`}</span>
            <span className="ampli-impact__fact">
              Redistribui <strong>{redistributed}</strong>
            </span>
          </span>
        </summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Mês</th>
              <th scope="col">Antes</th>
              <th scope="col">Depois</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.month}>
                <th scope="row">{competenceText(line.month)}</th>
                <td>{line.before}</td>
                <td>{line.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </li>
  );
};

const NotImpactedRow = ({ service }) => (
  <li data-testid="impact-not-impacted">
    {service.wbs ? <span className="ampli-impact__code">{service.wbs}</span> : null}
    <strong>{service.label}</strong>
    <span>{` — já governado por ${keyLabel(service.governingRuleKey)}`}</span>
  </li>
);

const Totals = ({ totals }) => {
  const redistributed = money(totals.redistributedCents);
  const before = money(totals.workBeforeCents);
  const after = money(totals.workAfterCents);
  return (
    <p data-testid="impact-totals">
      {`${totals.impactedCount.toLocaleString("pt-BR")} ${
        totals.impactedCount === 1 ? "serviço será alterado" : "serviços serão alterados"
      } · ${redistributed} redistribuídos · total da obra antes ${before}, depois ${after}${
        totals.workBeforeCents === totals.workAfterCents ? " (não muda)" : ""
      }`}
    </p>
  );
};

const RuleImpactSection = ({ impact, readOnly = false, onRetry }) => {
  if (readOnly) {
    return (
      <section aria-label="Impacto nos serviços" className="ampli-impact">
        <h3>Impacto nos serviços</h3>
        <p className="card-label">
          Obra em modo apresentação: a prévia de impacto não é oferecida.
        </p>
      </section>
    );
  }
  const answer = impact.answer;
  return (
    <section aria-label="Impacto nos serviços" className="ampli-impact">
      <h3>Impacto nos serviços</h3>
      {impact.status === "idle" ? (
        <p className="card-label">
          Complete a forma de desembolso para calcular o impacto nos serviços.
        </p>
      ) : null}
      {impact.status === "loading" ? (
        <p role="status">Calculando o impacto nos serviços…</p>
      ) : null}
      {impact.status === "error" ? (
        <div className="alert alert-error" role="alert">
          <p>Não foi possível calcular o impacto desta regra agora.</p>
          <button type="button" className="button button-outline small" onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      ) : null}
      {impact.status === "unavailable" ? (
        <p role="status">
          {`Não há como calcular o impacto agora: ${ampliInconsistentReasonLabel(impact.reason)} A regra pode ser salva e passa a valer quando houver uma origem utilizável.`}
        </p>
      ) : null}
      {impact.status === "ready" && answer ? (
        <>
          {answer.isEdit ? (
            <p className="card-label">
              A prévia compara a forma já gravada desta regra com a nova.
            </p>
          ) : null}
          {answer.totals.impactedCount === 0 ? (
            <p role="status" data-testid="impact-none">
              Nenhum serviço será alterado por esta regra. Ela ainda pode ser gravada.
            </p>
          ) : (
            <>
              <Totals totals={answer.totals} />
              <ul className="ampli-impact__services">
                {answer.impacted.map((service) => (
                  <ImpactedRow key={service.wbsCodeId} service={service} />
                ))}
              </ul>
            </>
          )}
          {answer.totals.truncated ? (
            <p className="card-label">
              {`A lista mostra os ${answer.impacted.length} maiores; os totais acima consideram todos.`}
            </p>
          ) : null}
          {answer.notImpacted.length > 0 ? (
            <div>
              <h4>{`Não impactados (${answer.totals.notImpactedCount.toLocaleString("pt-BR")})`}</h4>
              <p className="card-label">
                Serviços com o alvo cuja parte já é governada por uma regra mais específica.
              </p>
              <ul>
                {answer.notImpacted.map((service) => (
                  <NotImpactedRow key={service.wbsCodeId} service={service} />
                ))}
              </ul>
            </div>
          ) : null}
          {answer.totals.servicesWithoutComposition > 0 ? (
            <p className="card-label">
              {`${answer.totals.servicesWithoutComposition.toLocaleString("pt-BR")} ${
                answer.totals.servicesWithoutComposition === 1
                  ? "serviço não tem composição e não é alterado"
                  : "serviços não têm composição e não são alterados"
              } por regras de insumo.`}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default RuleImpactSection;
